import type { PortfolioReport } from '@interfaces/portfolioReport';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@ovComponents/2-component/accordion/accordion';
import { Box, Skeleton, Typography } from 'hephaestus/web';
import { ExpandMore as ExpandMoreIcon } from 'hephaestus/webIcons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  OptimizerAuditRollup,
  OptimizerAuditV2Record,
} from '../../types/audit.types';
import type { BulkRebalanceRun } from '../../types/run.types';
import { buildRunAdvisorInsights } from '../../utils/insights/buildRunAdvisorInsights';
import {
  buildRunRebalanceInsightAiContext,
  canGenerateRunInsightNarrative,
} from '../../utils/insights/buildRunRebalanceInsightAiContext';
import { buildRunInsightsCollapsedPreview } from '../../utils/presentation/formatRunImpactSummary';
import {
  BulkRebalanceInsightAiGenerateButton,
  BulkRebalanceInsightAiNarrative,
  BulkRebalanceInsightAiResult,
} from '../shared/insightAiNarrative';
import { BulkRebalanceRunImpactSection } from './runImpactSection';

export type BulkRebalanceRunInsightsPanelProps = {
  run: BulkRebalanceRun;
  rollup: OptimizerAuditRollup | null;
  audits: OptimizerAuditV2Record[];
  reports: PortfolioReport[];
  tickerMap?: Record<string, string>;
  loading: boolean;
  defaultExpanded?: boolean;
};

export const BulkRebalanceRunInsightsPanel = ({
  run,
  rollup,
  audits,
  reports,
  tickerMap = {},
  loading,
  defaultExpanded = false,
}: BulkRebalanceRunInsightsPanelProps) => {
  const { t } = useTranslation(['bulkRebalance']);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const showAi = canGenerateRunInsightNarrative(run, audits);
  const aiInput = useMemo(
    () => ({
      scope: 'RUN' as const,
      optimizerRunId: run.id,
      context: buildRunRebalanceInsightAiContext({
        run,
        audits,
        rollup,
        reports,
        tickerMap,
        t,
      }),
    }),
    [audits, reports, rollup, run, tickerMap, t],
  );

  const reportsById = useMemo(
    () => new Map(reports.map((report) => [report.id, report])),
    [reports],
  );
  const runAdvisorInsights = useMemo(
    () => buildRunAdvisorInsights(audits, reportsById),
    [audits, reportsById],
  );

  // Collapsed preview: interpretive batch shape only — pass/fail/trade totals live in the run header.
  const summaryLine = useMemo(
    () =>
      buildRunInsightsCollapsedPreview(
        runAdvisorInsights,
        t,
        run.portfolioSummary,
      ).join(' · '),
    [run.portfolioSummary, runAdvisorInsights, t],
  );

  if (loading) {
    return (
      <Box
        data-testid='bulk-rebalance-run-insights-loading'
        sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
      >
        <Skeleton variant='text' width='50%' />
        <Skeleton variant='text' width='70%' />
      </Box>
    );
  }

  if (!rollup && !run.portfolioSummary) {
    return null;
  }

  const panel = (
    <Accordion
      variant='outlined'
      disableGutters
      expanded={expanded}
      onChange={(_, nextExpanded) => setExpanded(nextExpanded)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon fontSize='small' />}
        data-testid='bulk-rebalance-run-insights-summary'
        sx={{
          '& .MuiAccordionSummary-content': {
            width: '100%',
            mr: 1,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            width: '100%',
            pr: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Typography variant='bodyMedium' sx={{ fontWeight: 600 }}>
              {t('runInsights.title')}
            </Typography>
            {summaryLine ? (
              <Typography variant='labelSmall' color='textSecondary'>
                {summaryLine}
              </Typography>
            ) : null}
          </Box>
          {showAi ? <BulkRebalanceInsightAiGenerateButton /> : null}
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {runAdvisorInsights ? (
            <BulkRebalanceRunImpactSection
              insights={runAdvisorInsights}
              audits={audits}
              tickerMap={tickerMap}
            />
          ) : null}
          {showAi ? <BulkRebalanceInsightAiResult /> : null}
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Box data-testid='bulk-rebalance-run-insights-panel'>
      {showAi ? (
        <BulkRebalanceInsightAiNarrative
          scope='run'
          input={aiInput}
          onGenerateStart={() => setExpanded(true)}
        >
          {panel}
        </BulkRebalanceInsightAiNarrative>
      ) : (
        panel
      )}
    </Box>
  );
};
